import { Injectable, inject } from '@angular/core';

import { CaseInsensitiveMap, Logger, NavigationService, PageService, RevertiblePageChange } from '@frontend/vanilla/core';
import { transform } from 'lodash-es';

import { MetaTagsConfig } from './meta-tags.client-config';
import { MetaTagsRule, PageMetaTagsRule, TagCollection } from './meta-tags.models';

const TITLE_CHANGE_KEY = '_vanilla:pageTitle_';

/**
 * @whatItDoes Manipulates page metadata (like title and meta tags).
 *
 * @howToUse
 *
 * ```
 * ngOnInit() {
 *     this.metaTagsService.setPageTags('Page Title', {
 *         description: 'This is awesome page.',
 *         author: 'Chuck Norris'
 *     }
 * }
 * ```
 *
 * @description
 *
 * You can use this service to change (or add) title and meta tags. All changes will survive only for particular page (until next navigation).
 * Values are automatically merged with configured ones by SEO analysts which take precedence.
 *
 * Values are automatically set for public pages displayed via `PageMatrixComponent`.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class MetaTagsService {
    private pageMetaTags: PageMetaTagsRule[];
    private globalMetaTags: MetaTagsRule[];
    private readonly globalChanges = new CaseInsensitiveMap<RevertiblePageChange>();
    private readonly customChanges = new CaseInsensitiveMap<RevertiblePageChange>();

    private readonly pageService = inject(PageService);
    private readonly config = inject(MetaTagsConfig);
    private readonly navigationService = inject(NavigationService);
    private readonly log = inject(Logger);

    /**
     * @internal
     */
    initialize() {
        // prepare tags - create regexes, normalize urls
        this.pageMetaTags = this.config.pageMetaTags.map<PageMetaTagsRule>((r: PageMetaTagsRule) => ({
            urlPath: normalizeUrlValue(r.urlPath),
            urlQueryParams: transform(
                r.urlQueryParams,
                (obj: Record<string, string>, val, name) => (obj[normalizeUrlValue(name)] = normalizeUrlValue(val)),
                {},
            ),
            title: r.title,
            tags: r.tags,
        }));
        this.globalMetaTags = this.config.globalMetaTags.map<MetaTagsRule>((r) => ({
            tags: r.tags,
            urlPathAndQueryRegexes: r.urlPathAndQueryRegexes.map((x: string) => {
                try {
                    return new RegExp(x, 'i');
                } catch (e) {
                    this.log.errorRemote(
                        `Failed to compile regex /${x}/ because it's not supported on this browser. So it won't be executed for SEO global meta tag rule "${r.ruleName}".`,
                        e,
                    );

                    return /.^/;
                }
            }),
        }));

        // register evaluation
        this.navigationService.locationChange.subscribe(() => this.evaluateSeoTags());
        this.evaluateSeoTags();
    }

    /**
     * Sets title and meta tags for current page (until next navigation).
     */
    setPageTags(title?: string, tags?: TagCollection) {
        this.applyTitle(title, this.customChanges);
        this.applyTags(tags || {}, this.customChanges);
    }

    /**
     * Clears values previously set by `setMetaTags`. This is usually done automatically on location change, but
     */
    clearPageTags() {
        this.clearCustomChanges();
    }

    private evaluateSeoTags() {
        // revert changes from previous page
        this.clearCustomChanges();
        this.clearGlobalChanges();

        // find rule matching current page
        const path = normalizeUrlValue(this.navigationService.location.path());
        const query = this.navigationService.location.search;
        const qs = query.toString();
        const pathAndQuery = decodeURI(path + (qs ? '?' + qs : ''));

        const pageRule = this.pageMetaTags.find(
            (r) =>
                r.urlPath === path &&
                Object.keys(r.urlQueryParams).every((name) => {
                    const val = query.get(name);
                    return val !== null && normalizeUrlValue(val) === r.urlQueryParams[name];
                }),
        );

        // apply page rule
        if (pageRule) {
            this.applyTitle(pageRule.title, this.globalChanges);
            this.applyTags(pageRule.tags, this.globalChanges);
        }

        // apply global rules
        for (const globalRule of this.globalMetaTags) {
            if (globalRule.urlPathAndQueryRegexes.some((r) => r.test(pathAndQuery))) {
                this.applyTags(globalRule.tags, this.globalChanges);
            }
        }
    }

    private clearCustomChanges() {
        this.customChanges.forEach((c) => c.revert());
        this.customChanges.clear();
    }

    private clearGlobalChanges() {
        this.globalChanges.forEach((c) => c.revert());
        this.globalChanges.clear();
    }

    private applyTitle(title: string | null | undefined, changes: Map<string, RevertiblePageChange>) {
        if (typeof title === 'string' && !this.changeExists(TITLE_CHANGE_KEY)) {
            const change = this.pageService.setTitle(title);
            changes.set(TITLE_CHANGE_KEY, change);
        }
    }

    private applyTags(tags: TagCollection, changes: Map<string, RevertiblePageChange>) {
        for (const name of Object.keys(tags)) {
            if (!name.trim().length) {
                throw new Error(`Tag name cannot be null nor white-space but such one was specified with value "${tags[name]}".`);
            }

            if (!this.changeExists(name)) {
                const change = this.pageService.setMeta(name, tags[name]);
                changes.set(name, change);
            }
        }
    }

    private changeExists(name: string): boolean {
        return this.globalChanges.has(name) || this.customChanges.has(name);
    }
}

function normalizeUrlValue(str: string): string {
    return str ? str.toLowerCase() : str;
}
